import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { batchAPI, journeyAPI } from '../../services/api'

export default function BatchJourneyBrowser({ jobId, onSelectionChange }) {
  const [journeys, setJourneys] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [sortField, setSortField] = useState('created_at')
  const [sortDir, setSortDir] = useState('desc')
  const [filterArchetype, setFilterArchetype] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    loadJourneys()
  }, [jobId])

  const loadJourneys = async () => {
    setLoading(true)
    try {
      let data
      if (jobId) {
        data = await batchAPI.getJobJourneys(jobId)
      } else {
        data = await journeyAPI.list()
        data = data.filter(j => j.status === 'completed')
      }
      setJourneys(data || [])
    } catch {
      setJourneys([])
    } finally {
      setLoading(false)
    }
  }

  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      if (onSelectionChange) onSelectionChange(Array.from(next))
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === journeys.length) {
      setSelectedIds(new Set())
      if (onSelectionChange) onSelectionChange([])
    } else {
      const all = new Set(journeys.map(j => j.id))
      setSelectedIds(all)
      if (onSelectionChange) onSelectionChange(Array.from(all))
    }
  }

  const handleExport = async (format) => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    try {
      await journeyAPI.exportBatch(ids, format)
    } catch {
      // Ignore
    }
  }

  // Extract unique archetypes for filtering
  const archetypes = [...new Set(journeys.map(j => {
    const config = j.persona_config || {}
    return config.archetype || 'Unknown'
  }).filter(Boolean))]

  // Filter and sort
  let filtered = journeys
  if (filterArchetype) {
    filtered = filtered.filter(j => {
      const config = j.persona_config || {}
      return (config.archetype || 'Unknown') === filterArchetype
    })
  }

  filtered = [...filtered].sort((a, b) => {
    let va = a[sortField], vb = b[sortField]
    if (sortField === 'total_episodes') {
      va = va || 0; vb = vb || 0
      return sortDir === 'asc' ? va - vb : vb - va
    }
    if (typeof va === 'string') {
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va)
    }
    return 0
  })

  if (loading) {
    return <div className="flex justify-center py-8"><span className="loading loading-spinner loading-md"></span></div>
  }

  if (journeys.length === 0) {
    return (
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body items-center text-center py-8">
          <p className="text-base-content/50">No journeys available. Run a batch generation first.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="card-title text-lg">Generated Conversations ({journeys.length})</h3>
          <div className="flex gap-2">
            {selectedIds.size > 0 && (
              <div className="dropdown dropdown-end">
                <label tabIndex={0} className="btn btn-outline btn-sm">Export Selected ({selectedIds.size})</label>
                <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-40">
                  <li><a onClick={() => handleExport('json')}>JSON</a></li>
                  <li><a onClick={() => handleExport('csv')}>CSV</a></li>
                </ul>
              </div>
            )}
            {archetypes.length > 1 && (
              <select
                className="select select-bordered select-sm"
                value={filterArchetype}
                onChange={e => setFilterArchetype(e.target.value)}
              >
                <option value="">All archetypes</option>
                {archetypes.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
          </div>
        </div>

        <div className="overflow-x-auto mt-2">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className="checkbox checkbox-xs"
                    checked={selectedIds.size === journeys.length && journeys.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="cursor-pointer" onClick={() => { setSortField('persona_name'); setSortDir(d => d === 'asc' ? 'desc' : 'asc') }}>
                  Persona {sortField === 'persona_name' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th>Patient</th>
                <th className="cursor-pointer" onClick={() => { setSortField('total_episodes'); setSortDir(d => d === 'asc' ? 'desc' : 'asc') }}>
                  Episodes {sortField === 'total_episodes' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th>Status</th>
                <th className="cursor-pointer" onClick={() => { setSortField('created_at'); setSortDir(d => d === 'asc' ? 'desc' : 'asc') }}>
                  Created {sortField === 'created_at' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(j => (
                <tr key={j.id} className={`hover ${selectedIds.has(j.id) ? 'bg-primary/5' : ''}`}>
                  <td>
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs"
                      checked={selectedIds.has(j.id)}
                      onChange={() => toggleSelection(j.id)}
                    />
                  </td>
                  <td className="font-medium">{j.persona_name}</td>
                  <td>{j.patient_name}</td>
                  <td>{j.total_episodes}</td>
                  <td>
                    <span className={`badge badge-sm badge-outline px-3 ${j.status === 'completed' ? 'badge-success' : j.status === 'failed' ? 'badge-error' : 'badge-warning'}`}>
                      {j.status}
                    </span>
                  </td>
                  <td className="text-xs">{new Date(j.created_at).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="btn btn-ghost btn-xs"
                      onClick={() => navigate(`/generator/journey/${j.id}`)}
                      disabled={j.status !== 'completed'}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
